import { useMemo } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { resources } from "@constants/hardcoded-strings";
import { VERIFICATION_SERVICES_DETAILS_URL } from "@constants/routes";
import { useModalContext } from "@context/use-modal-context";
import { ChangeRequestStatusTypes } from "@enums/change-request-status-types";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { ModalTypes } from "@enums/modal-types";
import { FormattedVerificationServiceConfiguration } from "@idw-types/data/verification-service-configuration";
import {
    AxnAvailablePropertyCategory,
    AxnAttributeTypeField,
    AxnCountry,
    AxnIdentityProvider,
    AxnCompany,
    AxnRelyingParty,
    SimpleAxnWorkflow,
    AxnAttributeTypeFieldCustomization
} from "@idw-types/data/reference-data/axn";
import {
    RpServiceType,
    SelfServiceVerificationAttribute,
    AttributeClassification,
    AttributeLabel,
    AttributeType,
    RpServiceAttributePropertyMap,
    RpServiceAttributeProperty
} from "@idw-types/data/reference-data/generic";
import { CellDropdownItem } from "@idw-types/common";
import { store } from "@redux/store";
import { getVerificationServiceConfigurationsReduxRequest, updateVerificationServiceChangeRequestReduxRequest } from "@redux-requests/verification-services";
import { createVerificationServiceChangeRequestRequest, exportRpServiceConfigurationRequest } from "@requests/service-configuration";
import { downloadVerificationServiceConfigurationFile } from "@utils/file-util";
import { displaySuccessModal, logAndDisplayErrorModal } from "@utils/app-alert-util";
import { injectRpServiceId } from "@utils/parameter-injector";
import { navigateToUtil, openToNewTabUtil } from "@utils/navigation-util";
import { getTableStateQueryParameters } from "@utils/table-util";
import {
    clickVerificationServiceChangeRequestActionHandler, deployVerificationService
} from "@utils/change-request-modal-util/verification-service-change-request-modal-util";
import { getRemainingChangeRequestStatuses } from "@utils/change-request-modal-util/common-change-request-modal-util";
import { TableCellWithDropdown } from "../../common/table-cell-with-dropdown/table-cell-with-dropdown";
import { VerificationLinkProviderModal } from "../../modals/verification-link-provider-modal/verification-link-provider-modal";
import { ArchiveVerificationServiceModal } from "../archive-verification-service-modal/archive-verification-service-modal";
import { RestoreVerificationServiceModal } from "../restore-verification-service-modal/restore-verification-service-modal";
import { DeleteVerificationServiceModal } from "../delete-verification-service-modal/delete-verification-service-modal";
import styles from "./verification-services-table-cell.less";
import { FormattedMessageTemplate } from "@idw-types/data/message-template";

type VerificationServicesTableCellProps = {
    value: string;
    verificationServiceConfiguration: FormattedVerificationServiceConfiguration;
    axnCompanies: Array<AxnCompany>;
    axnCountries: Array<AxnCountry>;
    axnIdentityProviders: Array<AxnIdentityProvider>;
    axnAvailablePropertyCategories: Array<AxnAvailablePropertyCategory>;
    messageTemplates: Array<FormattedMessageTemplate>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    axnAttributeTypeFieldCustomizations: Array<AxnAttributeTypeFieldCustomization>;
    selfServiceVerificationAttributes: Array<SelfServiceVerificationAttribute>;
    relyingParties: Array<AxnRelyingParty>;
    rpServiceTypes: Array<RpServiceType>;
    simpleAXNWorkflows: Array<SimpleAxnWorkflow>;
    simpleAXNAdminWorkflows: Array<SimpleAxnWorkflow>;
    attributeTypes: Array<AttributeType>;
    attributeLabels: Array<AttributeLabel>;
    attributeClassifications: Array<AttributeClassification>;
    rpServiceAttributeProperties: Array<RpServiceAttributeProperty>;
    rpServiceAttributePropertyMaps: Array<RpServiceAttributePropertyMap>;
};

type VerificationServicesModalProps = {
    modalType: ModalTypes;
    resultModalType: ModalTypes;
    title: string;
    description: string;
    buttonLabel: string;
};

export const VerificationServicesTableCell = (props: VerificationServicesTableCellProps) => {
    const {
        value,
        verificationServiceConfiguration,
        axnCompanies,
        axnCountries,
        axnIdentityProviders,
        messageTemplates,
        axnAttributeTypeFields,
        axnAttributeTypeFieldCustomizations,
        axnAvailablePropertyCategories,
        selfServiceVerificationAttributes,
        relyingParties,
        rpServiceTypes,
        simpleAXNWorkflows,
        simpleAXNAdminWorkflows,
        attributeClassifications,
        attributeLabels,
        attributeTypes,
        rpServiceAttributeProperties,
        rpServiceAttributePropertyMaps
    } = props;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const modalContext = useModalContext();

    const renderVerificationServiceModal = (workflowsModalProps: VerificationServicesModalProps) => {
        if (verificationServiceConfiguration.id) {
            const renderVerificationLinkProviderModal = (onModalClose: () => void) => {
                return (
                    <VerificationLinkProviderModal
                        {...workflowsModalProps}
                        idPrefix={"verification-service"}
                        onModalClose={onModalClose}
                        rpServiceId={verificationServiceConfiguration.id}
                        canModifySelfServiceVerificationExpiration={verificationServiceConfiguration.permissions.canModifySelfServiceVerificationExpiration}
                        axnCompanies={axnCompanies}
                        axnCountries={axnCountries}
                        axnIdentityProviders={axnIdentityProviders}
                        axnAvailablePropertyCategories={axnAvailablePropertyCategories}
                        messageTemplates={messageTemplates}
                        axnAttributeTypeFields={axnAttributeTypeFields}
                        axnAttributeTypeFieldCustomizations={axnAttributeTypeFieldCustomizations}
                        selfServiceVerificationAttributes={selfServiceVerificationAttributes}
                        attributeClassifications={attributeClassifications}
                        attributeLabels={attributeLabels}
                        attributeTypes={attributeTypes}
                        rpServiceAttributeProperties={rpServiceAttributeProperties}
                        rpServiceAttributePropertyMaps={rpServiceAttributePropertyMaps}
                    />
                );
            };

            modalContext.setModalState({
                modalType: workflowsModalProps.modalType,
                renderFunction: renderVerificationLinkProviderModal
            });
        } else {
            logAndDisplayErrorModal(
                dispatch,
                null,
                resources.pages.verificationServices.errors.invalidConfiguration.title,
                resources.pages.verificationServices.errors.invalidConfiguration.message
            );
        }
    }

    const onClickTryNow = () => {
        renderVerificationServiceModal({
            modalType: ModalTypes.TryNow,
            resultModalType: ModalTypes.TryNowResult,
            title: resources.modals.verificationLinkProvider.titles.tryNow,
            description: resources.modals.verificationLinkProvider.descriptions.tryNow,
            buttonLabel: resources.modals.verificationLinkProvider.buttonLabels.tryNow,
        });
    };

    const onClickCopyLink = () => {
        renderVerificationServiceModal({
            modalType: ModalTypes.CopyLink,
            resultModalType: ModalTypes.CopyLinkResult,
            title: resources.modals.verificationLinkProvider.titles.copyLink,
            description: resources.modals.verificationLinkProvider.descriptions.copyLink,
            buttonLabel: resources.modals.verificationLinkProvider.buttonLabels.copyLink,
        });
    };

    const onClickSendMessageWithLink = () => {
        renderVerificationServiceModal({
            modalType: ModalTypes.SendMessageWithLink,
            resultModalType: ModalTypes.SendMessageWithLinkResult,
            title: resources.modals.verificationLinkProvider.titles.sendLink,
            description: resources.modals.verificationLinkProvider.descriptions.sendLink,
            buttonLabel: resources.modals.verificationLinkProvider.buttonLabels.sendLink,
        });
    };

    const onClickOpen = () => {
        const tableState = store.getState().tableSettings.verificationServices;
        const url = injectRpServiceId(VERIFICATION_SERVICES_DETAILS_URL, verificationServiceConfiguration.id);
        let tableStateQueryParams = getTableStateQueryParameters(tableState);
        if (tableStateQueryParams.length > 0) {
            tableStateQueryParams = tableStateQueryParams.substring(1, tableStateQueryParams.length);
        }
        openToNewTabUtil(`${url}?${tableStateQueryParams}`, routerLocation.pathname);
    };

    const onSubmitCreateVerificationServiceChangeRequest = async (onModalClose: () => void) => {
        const { data, error } = await createVerificationServiceChangeRequestRequest(verificationServiceConfiguration.id);
        if (data?.changeRequest) {
            displaySuccessModal(
                dispatch,
                resources.requests.createVerificationServiceChangeRequest.success.title,
                resources.requests.createVerificationServiceChangeRequest.success.message
            );
            onModalClose();
            const url = injectRpServiceId(VERIFICATION_SERVICES_DETAILS_URL, data.changeRequest.rpServiceID);
            navigateToUtil(url, navigate, routerLocation.pathname);
            return data.changeRequest;
        } else if (error) {
            logAndDisplayErrorModal(
                dispatch,
                error,
                resources.requests.createVerificationServiceChangeRequest.error.title,
                resources.requests.createVerificationServiceChangeRequest.error.message,
                error
            );
        }
        return null;
    };

    const onClickCreateAModifyChangeRequest = () => {
        clickVerificationServiceChangeRequestActionHandler(
            dispatch,
            onSubmitCreateVerificationServiceChangeRequest,
            modalContext,
            verificationServiceConfiguration.id,
            verificationServiceConfiguration.serviceName,
            verificationServiceConfiguration.apiKey,
            ModalTypes.CreateVerificationServiceChangeRequest
        );
    };

    const onSubmitSubmitVerificationServiceChangeRequest = async (onModalClose: () => void) => {
        const response = await updateVerificationServiceChangeRequestReduxRequest(dispatch)(
            verificationServiceConfiguration.id,
            null,
            ChangeRequestStatusTypes.SUBMITTED,
            axnCompanies,
            relyingParties,
            rpServiceTypes,
            simpleAXNWorkflows,
            simpleAXNAdminWorkflows
        );
        if (response) {
            onModalClose();
        }
        return response;
    };

    const onClickSubmitChangeRequest = () => {
        clickVerificationServiceChangeRequestActionHandler(
            dispatch,
            onSubmitSubmitVerificationServiceChangeRequest,
            modalContext,
            verificationServiceConfiguration.id,
            verificationServiceConfiguration.serviceName,
            verificationServiceConfiguration.apiKey,
            ModalTypes.SubmitVerificationServiceChangeRequest
        );
    };

    const onSubmitCancelVerificationServiceChangeRequest = async (onModalClose: () => void) => {
        const response = await updateVerificationServiceChangeRequestReduxRequest(dispatch)(
            verificationServiceConfiguration.id,
            null,
            ChangeRequestStatusTypes.CANCELED,
            axnCompanies,
            relyingParties,
            rpServiceTypes,
            simpleAXNWorkflows,
            simpleAXNAdminWorkflows
        );
        if (response) {
            onModalClose();
        }
        return response;
    };

    const onClickCancelChangeRequest = () => {
        clickVerificationServiceChangeRequestActionHandler(
            dispatch,
            onSubmitCancelVerificationServiceChangeRequest,
            modalContext,
            verificationServiceConfiguration.id,
            verificationServiceConfiguration.serviceName,
            verificationServiceConfiguration.apiKey,
            ModalTypes.CancelVerificationServiceChangeRequest
        );
    };

    const onSubmitUnsubmitVerificationServiceChangeRequest = async (onModalClose: () => void) => {
        const response = await updateVerificationServiceChangeRequestReduxRequest(dispatch)(
            verificationServiceConfiguration.id,
            null,
            ChangeRequestStatusTypes.UNSUBMITTED,
            axnCompanies,
            relyingParties,
            rpServiceTypes,
            simpleAXNWorkflows,
            simpleAXNAdminWorkflows
        );
        if (response) {
            onModalClose();
        }
        return response;
    };

    const onClickUnsubmitChangeRequest = () => {
        clickVerificationServiceChangeRequestActionHandler(
            dispatch,
            onSubmitUnsubmitVerificationServiceChangeRequest,
            modalContext,
            verificationServiceConfiguration.id,
            verificationServiceConfiguration.serviceName,
            verificationServiceConfiguration.apiKey,
            ModalTypes.UnsubmitVerificationServiceChangeRequest
        );
    };

    const onClickReviewChangeRequest = async () => {
        if (verificationServiceConfiguration.changeRequestStatus !== ChangeRequestStatusTypes.UNDER_REVIEW) {
            const response = await updateVerificationServiceChangeRequestReduxRequest(dispatch)(
                verificationServiceConfiguration.id,
                null,
                ChangeRequestStatusTypes.UNDER_REVIEW,
                axnCompanies,
                relyingParties,
                rpServiceTypes,
                simpleAXNWorkflows,
                simpleAXNAdminWorkflows
            );

            if (response) {
                const url = injectRpServiceId(VERIFICATION_SERVICES_DETAILS_URL, verificationServiceConfiguration.id);
                navigateToUtil(url, navigate, routerLocation.pathname);
            }
        } else {
            const url = injectRpServiceId(VERIFICATION_SERVICES_DETAILS_URL, verificationServiceConfiguration.id);
            navigateToUtil(url, navigate, routerLocation.pathname);
        }
    };

    // const onClickApproveChangeRequest = () => {

    // };

    // const onClickRejectChangeRequest = () => {

    // };

    const onSubmitDeployNowVerificationServiceChangeRequest = async (onModalClose: () => void) => {
        const remainingChangeRequestStatuses = getRemainingChangeRequestStatuses(verificationServiceConfiguration.changeRequestStatus);
        const response = await deployVerificationService(
            dispatch,
            verificationServiceConfiguration.id,
            remainingChangeRequestStatuses,
            0
        );

        if (response) {
            displaySuccessModal(
                dispatch,
                resources.requests.deployWorkflowChangeRequest.success.title,
                resources.requests.deployWorkflowChangeRequest.success.message
            );
        }

        await getVerificationServiceConfigurationsReduxRequest(dispatch)(
            axnCompanies,
            relyingParties,
            rpServiceTypes,
            simpleAXNWorkflows,
            simpleAXNAdminWorkflows
        );

        if (response) {
            onModalClose();
        }

        return response;
    }

    const onClickDeployChangesNow = () => {
        clickVerificationServiceChangeRequestActionHandler(
            dispatch,
            onSubmitDeployNowVerificationServiceChangeRequest,
            modalContext,
            verificationServiceConfiguration.id,
            verificationServiceConfiguration.serviceName,
            verificationServiceConfiguration.apiKey,
            ModalTypes.DeployNowVerificationServiceChangeRequest
        );
    };

    const onClickExportServiceConfiguration = async () => {
        if (verificationServiceConfiguration.id) {
            const { data, error } = await exportRpServiceConfigurationRequest(verificationServiceConfiguration.id);
            if (data) {
                downloadVerificationServiceConfigurationFile(data.serviceName, data);
            } else if (error) {
                logAndDisplayErrorModal(
                    dispatch,
                    error,
                    resources.requests.exportVerificationServiceConfiguration.error.title,
                    resources.requests.exportVerificationServiceConfiguration.error.message
                );
            }
        } else {
            logAndDisplayErrorModal(
                dispatch,
                null,
                resources.pages.verificationServices.errors.invalidConfiguration.title,
                resources.pages.verificationServices.errors.invalidConfiguration.message
            );
        }
    };

    const onClickArchiveService = () => {
        if (verificationServiceConfiguration.id) {
            const renderArchiveVerificationServiceModal = (onModalClose: () => void) => {
                return (
                    <ArchiveVerificationServiceModal
                        onModalClose={onModalClose}
                        verificationServiceConfiguration={verificationServiceConfiguration}
                    />
                );
            };

            modalContext.setModalState({
                modalType: ModalTypes.ArchiveVerificationService,
                renderFunction: renderArchiveVerificationServiceModal
            });
        } else {
            logAndDisplayErrorModal(
                dispatch,
                null,
                resources.pages.verificationServices.errors.invalidConfiguration.title,
                resources.pages.verificationServices.errors.invalidConfiguration.message,
            );
        }
    };

    const onClickRestoreService = () => {
        if (verificationServiceConfiguration.id) {
            const renderRestoreVerificationServiceModal = (onModalClose: () => void) => {
                return (
                    <RestoreVerificationServiceModal
                        onModalClose={onModalClose}
                        verificationServiceConfiguration={verificationServiceConfiguration}
                    />
                );
            };

            modalContext.setModalState({
                modalType: ModalTypes.RestoreVerificationService,
                renderFunction: renderRestoreVerificationServiceModal
            });
        } else {
            logAndDisplayErrorModal(
                dispatch,
                null,
                resources.pages.verificationServices.errors.invalidConfiguration.title,
                resources.pages.verificationServices.errors.invalidConfiguration.message,
            );
        }
    };

    const onClickDeleteService = () => {
        if (verificationServiceConfiguration.id) {
            const renderDeleteVerificationServiceModal = (onModalClose: () => void) => {
                return (
                    <DeleteVerificationServiceModal
                        onModalClose={onModalClose}
                        verificationServiceConfiguration={verificationServiceConfiguration}
                    />
                );
            };

            modalContext.setModalState({
                modalType: ModalTypes.DeleteVerificationService,
                renderFunction: renderDeleteVerificationServiceModal
            });
        } else {
            logAndDisplayErrorModal(
                dispatch,
                null,
                resources.pages.verificationServices.errors.invalidConfiguration.title,
                resources.pages.verificationServices.errors.invalidConfiguration.message,
            );
        }
    };

    const verificationServiceDropdownItems = useMemo(() => {
        const items: Array<CellDropdownItem> = [];
        const {
            status,
            changeRequestType,
            changeRequestStatus,
            permissions,
            deployed
        } = verificationServiceConfiguration;

        if (status === EntityStatusTypes.INACTIVE) {
            if (permissions.canRestore) {
                items.push({
                    idSuffix: 'restore-service',
                    title: resources.tables.verificationServices.dropdownItems.restoreService.label,
                    text: resources.tables.verificationServices.dropdownItems.restoreService.label,
                    onClick: onClickRestoreService
                });
            }

            if (permissions.canDelete) {
                items.push({
                    idSuffix: 'delete-service',
                    title: resources.tables.verificationServices.dropdownItems.deleteService.label,
                    text: resources.tables.verificationServices.dropdownItems.deleteService.label,
                    className: styles.archiveButton,
                    onClick: onClickDeleteService
                });
            }
        } else if (status === EntityStatusTypes.ACTIVE) {
            if (deployed) {
                items.push(
                    {
                        idSuffix: `service-execution-header`,
                        title: resources.common.dropdownItems.execution.label,
                        text: resources.common.dropdownItems.execution.label,
                        onClick: () => { },
                        isHeader: true
                    },
                    {
                        idSuffix: 'try-now',
                        title: resources.common.dropdownItems.tryNow.label,
                        text: resources.common.dropdownItems.tryNow.label,
                        onClick: onClickTryNow
                    },
                    {
                        idSuffix: 'copy-link',
                        title: resources.common.dropdownItems.copyLink.label,
                        text: resources.common.dropdownItems.copyLink.label,
                        onClick: onClickCopyLink
                    },
                    {
                        idSuffix: 'send-message',
                        title: resources.common.dropdownItems.sendMessage.label,
                        text: resources.common.dropdownItems.sendMessage.label,
                        onClick: onClickSendMessageWithLink
                    }
                );
            }

            items.push({
                idSuffix: `service-configuration-header`,
                title: resources.common.dropdownItems.configuration.label,
                text: resources.common.dropdownItems.configuration.label,
                onClick: () => { },
                isHeader: true
            });

            if (permissions.canView) {
                items.push({
                    idSuffix: 'open',
                    title: resources.pages.verificationServices.dropdownItems.open.label,
                    text: resources.pages.verificationServices.dropdownItems.open.label,
                    onClick: onClickOpen
                });
            }

            if ((
                permissions.canSubmit && permissions.canReview && permissions.canDeploy && changeRequestStatus &&
                [
                    ChangeRequestStatusTypes.UNSUBMITTED,
                    ChangeRequestStatusTypes.SUBMITTED,
                    ChangeRequestStatusTypes.UNDER_REVIEW,
                    ChangeRequestStatusTypes.APPROVED
                ].includes(changeRequestStatus)
            ) || (permissions.canDeploy && changeRequestType && changeRequestStatus === ChangeRequestStatusTypes.APPROVED)) {
                items.push({
                    idSuffix: 'deploy-changes-now',
                    title: resources.common.dropdownItems.deployChangesNow.label,
                    text: resources.common.dropdownItems.deployChangesNow.label,
                    onClick: onClickDeployChangesNow
                });
            }

            if (permissions.canModify && !changeRequestType && !changeRequestStatus) {
                items.push({
                    idSuffix: 'create-modify-change-request',
                    title: resources.common.dropdownItems.createModifyChangeRequest.label,
                    text: resources.common.dropdownItems.createModifyChangeRequest.label,
                    onClick: onClickCreateAModifyChangeRequest
                });
            }

            if (permissions.canSubmit && changeRequestType && changeRequestStatus === ChangeRequestStatusTypes.UNSUBMITTED) {
                items.push({
                    idSuffix: 'submit-change-request',
                    title: resources.common.dropdownItems.submitChangeRequestForReview.label,
                    text: resources.common.dropdownItems.submitChangeRequestForReview.label,
                    onClick: onClickSubmitChangeRequest
                });
            }

            if (
                permissions.canSubmit &&
                changeRequestType &&
                [ChangeRequestStatusTypes.SUBMITTED, ChangeRequestStatusTypes.UNDER_REVIEW].includes(changeRequestStatus)
            ) {
                items.push({
                    idSuffix: 'unsubmit-change-request',
                    title: resources.common.dropdownItems.unsubmitChangeRequest.label,
                    text: resources.common.dropdownItems.unsubmitChangeRequest.label,
                    onClick: onClickUnsubmitChangeRequest
                });
            }

            if (
                permissions.canReview &&
                changeRequestType &&
                [ChangeRequestStatusTypes.SUBMITTED, ChangeRequestStatusTypes.UNDER_REVIEW].includes(changeRequestStatus)
            ) {
                items.push(
                    {
                        idSuffix: 'review-change-request',
                        title: resources.common.dropdownItems.reviewChangeRequest.label,
                        text: resources.common.dropdownItems.reviewChangeRequest.label,
                        onClick: onClickReviewChangeRequest
                    },
                    // {
                    //     title: resources.common.dropdownItems.approveChangeRequest.label,
                    //     text: resources.common.dropdownItems.approveChangeRequest.label,
                    //     onClick: onClickApproveChangeRequest
                    // },
                    // {
                    //     title: resources.common.dropdownItems.rejectChangeRequest.label,
                    //     text: resources.common.dropdownItems.rejectChangeRequest.label,
                    //     onClick: onClickRejectChangeRequest
                    // }
                );
            }

            if (
                permissions.canCancel &&
                changeRequestType &&
                ![ChangeRequestStatusTypes.CANCELED, ChangeRequestStatusTypes.DEPLOYED].includes(changeRequestStatus)
            ) {
                items.push({
                    idSuffix: 'cancel-change-request',
                    title: resources.common.dropdownItems.cancelChangeRequest.label,
                    text: resources.common.dropdownItems.cancelChangeRequest.label,
                    onClick: onClickCancelChangeRequest
                });
            }

            if (permissions.canView) {
                items.push({
                    idSuffix: 'export-service-configuration',
                    title: resources.tables.verificationServices.dropdownItems.exportServiceConfiguration.label,
                    text: resources.tables.verificationServices.dropdownItems.exportServiceConfiguration.label,
                    onClick: onClickExportServiceConfiguration
                });
            }

            if (deployed && permissions.canArchive) {
                items.push({
                    idSuffix: 'archive-service',
                    title: resources.tables.verificationServices.dropdownItems.archiveService.label,
                    text: resources.tables.verificationServices.dropdownItems.archiveService.label,
                    className: styles.archiveButton,
                    onClick: onClickArchiveService
                });
            }

            if (deployed && permissions.canDelete) {
                items.push({
                    idSuffix: 'delete-service',
                    title: resources.tables.verificationServices.dropdownItems.deleteService.label,
                    text: resources.tables.verificationServices.dropdownItems.deleteService.label,
                    className: styles.archiveButton,
                    onClick: onClickDeleteService
                });
            }
        }

        return items;
    }, [verificationServiceConfiguration]);

    return (
        <TableCellWithDropdown
            value={value}
            dropdownText={""}
            items={verificationServiceDropdownItems}
            uniqueId={verificationServiceConfiguration.id}
        />
    );
};